import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Plus, Briefcase, User, Building2, ShoppingBag, Loader2
} from 'lucide-react';
import { auth } from '../utils/auth';
import axios from 'axios';
import { toast } from 'sonner';
import CasesViewMinimal from '../components/lexy/CasesViewMinimal';
import MarketplaceAFATab from '../components/lexy/MarketplaceAFATab';
import CreateCaseModal from '../components/modals/CreateCaseModal';
import { casesApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CasesWithTabs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = auth.getUser();
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  
  const [activeTab, setActiveTab] = useState(searchParams.get('view') || 'mine');
  const [myCases, setMyCases] = useState([]);
  const [teamCases, setTeamCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [afaProfileStatus, setAfaProfileStatus] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [caseFilter, setCaseFilter] = useState('all');
  
  const hasTeamAccess = user && ['LAWYER', 'ADMIN', 'TENANT_ADMIN', 'PARTNER', 'TEAM_LEAD'].includes(user.role);
  const hasAFAAccess = user && ['LAWYER', 'TENANT_ADMIN', 'ADMIN', 'PARTNER'].includes(user.role) && afaProfileStatus === 'APPROVED';

  useEffect(() => {
    if (!user) {
      navigate('/lexy/login');
      return;
    }
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const token = auth.getToken();
      
      const myCasesRes = await axios.get(`${API}/cases`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { member_id: user.id, view: 'my_cases' }
      });
      setMyCases(myCasesRes.data);
      
      if (hasTeamAccess) {
        try {
          const teamCasesRes = await axios.get(`${API}/cases`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { view: 'team_cases' }
          });
          setTeamCases(teamCasesRes.data);
        } catch (e) {
          console.log('Team cases not available');
        }
      }
      
      // Check AFA status
      try {
        const afaRes = await axios.get(`${API}/afa/profile/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAfaProfileStatus(afaRes.data?.status);
      } catch (e) {
        setAfaProfileStatus(null);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Kunne ikke hente saker');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    setStatusFilter('all');
    setCaseFilter('all');
  };

  const handleNavigate = (view) => {
    navigate(`/lexy/dashboard?tab=${view}`);
  };

  const currentCases = activeTab === 'mine' ? myCases : teamCases;

  const tabs = [
    { id: 'mine', label: 'My Cases', icon: User, count: myCases.length, visible: true },
    { id: 'team', label: 'Team', icon: Building2, count: teamCases.length, visible: hasTeamAccess },
    { id: 'afa', label: 'AFA Market', icon: ShoppingBag, visible: hasAFAAccess }
  ].filter(t => t.visible);

  if (loading) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading cases...</p>
        </div>
      </div>
    );
  }

  // If there are tabs (team/AFA access), show tab selector at the top
  const showTabSelector = tabs.length > 1;

  return (
    <div className="h-full bg-white overflow-y-auto overflow-x-hidden">
      {/* Tab selector if multiple tabs available - Minimalist underline style */}
      {showTabSelector && (
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-6">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`relative flex items-center gap-2 py-3 text-sm font-medium transition-colors ${
                      isActive 
                        ? 'text-gray-900' 
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {tab.label}
                    {tab.count !== undefined && (
                      <span className={`text-xs tabular-nums ${
                        isActive ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                    {/* Active indicator line */}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-gray-900 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === 'afa' ? (
            <div className="max-w-4xl mx-auto px-6 sm:px-8 py-8">
              <MarketplaceAFATab navigate={navigate} />
            </div>
          ) : (
            <CasesViewMinimal
              allCases={currentCases}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              caseFilter={caseFilter}
              setCaseFilter={setCaseFilter}
              navigate={navigate}
              onCreateCase={() => setShowCreateCaseModal(true)}
            />
          )}
        </motion.div>
      </AnimatePresence>
      
      {/* Create Case Modal */}
      <CreateCaseModal 
        isOpen={showCreateCaseModal}
        onClose={() => setShowCreateCaseModal(false)}
        onSuccess={(caseId) => {
          setShowCreateCaseModal(false);
          fetchCases();
        }}
      />
    </div>
  );
};

export default CasesWithTabs;
